<template>
  <div class="container p-4 mx-auto">
    <div v-if="loading" class="flex justify-center items-center min-h-[200px]">
      <div class="w-12 h-12 border-b-2 rounded-full animate-spin border-custom-yellow"></div>
    </div>
    <div v-else>
    <AlertLayout title="Reminders!"
      message="Kindly note that only blood units registered on Damu-Sasa will be found by the search.<br>Non-registered units should 1st be registered."
      type="warning" class="mb-4" />
    <div class="p-5 bg-white border border-t-0 rounded-lg shadow-md">
      <h2 class="mb-4 text-xl">
        <span v-if="flag === 'Split'">Unit Splitting</span>
        <span v-else-if="flag === 'Requote'">Unit Requote</span>
        <span v-else-if="flag === 'Leuco-reduction'">Unit Leucoreduction</span>
        <span v-else>Component Preparation</span>
      </h2>
      <div class="mb-8">
        <h3 class="mb-2 text-lg font-bold">Source Units</h3>
        <div class="overflow-x-auto">
          <table class="min-w-full border border-gray-200 divide-y divide-gray-200 rounded-lg shadow-md">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-xs tracking-wider text-left">Serial Number</th>
                <th class="px-6 py-3 text-xs tracking-wider text-left">Blood Group</th>
                <th class="px-6 py-3 text-xs tracking-wider text-left">Blood Product</th>
                <th class="px-6 py-3 text-xs tracking-wider text-left">Volume</th>
                <th class="px-6 py-3 text-xs tracking-wider text-left">Expiry Date</th>
                <th class="px-6 py-3 text-xs tracking-wider text-left"></th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(unit, index) in source_units.length > 0 ? source_units : [source_unit]" :key="unit.product_id">
                <td class="px-6 py-4 text-sm whitespace-nowrap">{{ unit.pint_serial }}</td>
                <td class="px-6 py-4 text-sm whitespace-nowrap">{{ unit.blood_group?.description }}</td>
                <td class="px-6 py-4 text-sm whitespace-nowrap">{{ unit.unit_product?.name }}</td>
                <td class="px-6 py-4 text-sm whitespace-nowrap">{{ unit.volume }} ml</td>
                <td class="px-6 py-4 text-sm whitespace-nowrap">{{ unit.expiry_date ? new Date(unit.expiry_date).toLocaleDateString() : '-' }}</td>
                <td class="px-6 py-4 text-sm whitespace-nowrap">
                  <button 
                    v-if="source_units.length > 1" 
                    type="button" 
                    @click="removeSourceUnit(index)"
                    class="p-1 ml-1 border rounded text-custom-yellow border-custom-yellow"
                    title="Remove unit">
                    <svg class="w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z" />
                  </svg>
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot v-if="source_units.length > 1" class="bg-gray-50">
              <tr>
                <td colspan="5" class="px-6 py-3 font-bold text-right">Total Volume:</td>
                <td colspan="2" class="px-6 py-3 font-bold">{{ totalSourceVolume }} ml</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

<!-- Centrifuge Settings -->
      <div v-if="flag === 'Split'" class="p-6 mb-6 bg-gray-100 rounded-lg">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-bold">Centrifuge Settings</h3>
          <button type="button" @click="addCentrifugeSetting" class="px-3 py-1 text-sm text-white rounded bg-custom-yellow">
            Add Centrifuge Setting
          </button>
        </div>
        <div v-for="(setting, idx) in centrifugeSettings" :key="idx" class="p-3 mb-3 bg-white border border-gray-300 rounded">
          <h4 class="mb-2 text-sm font-semibold">Setting {{ idx + 1 }}</h4>
          <div class="grid grid-cols-3 gap-2 md:grid-cols-4">
            <div>
              <label class="block mb-1 text-xs text-gray-700" :for="`centrifuge_device_${idx}`">Centrifuge Device</label>
              <select v-model="setting.centrifuge_device" class="block w-full px-2 py-1 text-sm border rounded-md" @change="handleCentrifugeDeviceChange(setting, idx)">
                <option value="">--Select centrifuge--</option>
                <option v-for="item in centrifugeDevices" :key="item.id" :value="item.id">
                  {{ item.item_name }}
                </option>
              </select>
            </div>
            <div>
              <label class="block mb-1 text-xs text-gray-700" :for="`centrifuge_code_${idx}`">Centrifuge Code</label>
              <input v-model="setting.centrifuge_code" type="text" readonly class="block w-full px-2 py-1 text-sm bg-gray-200 border rounded-md">
            </div>
            <div>
              <label class="block mb-1 text-xs text-gray-700" :for="`centrifuge_batch_${idx}`">Centrifuge Batch</label>
              <input v-model="setting.centrifuge_batch" type="text" readonly class="block w-full px-2 py-1 text-sm bg-gray-200 border rounded-md">
            </div>
            <div>
              <label class="block mb-1 text-xs text-gray-700" :for="`speed_${idx}`">Speed (RPM)</label>
              <input v-model.number="setting.speed" type="number" placeholder="RPM" class="block w-full px-2 py-1 text-sm border rounded-md">
            </div>
            <div>
              <label class="block mb-1 text-xs text-gray-700" :for="`centrifuge_time_${idx}`">Time (min)</label>
              <input v-model.number="setting.centrifuge_time" type="number" placeholder="Time" class="block w-full px-2 py-1 text-sm border rounded-md">
            </div>
            <div>
              <label class="block mb-1 text-xs text-gray-700" :for="`temperature_${idx}`">Temperature (°C)</label>
              <input v-model.number="setting.temperature" type="number" step="0.1" placeholder="Temp" class="block w-full px-2 py-1 text-sm border rounded-md">
            </div>
            <div>
              <label class="block mb-1 text-xs text-gray-700" :for="`brake_${idx}`">Brake</label>
              <select v-model="setting.brake" class="block w-full px-2 py-1 text-sm border rounded-md">
                <option value="">--Select brake--</option>
                <option value="Soft">Soft</option>
                <option value="Hard">Hard</option>
              </select>
            </div>
            <div>
              <label class="block mb-1 text-xs text-gray-700" :for="`spin_type_${idx}`">Spin Type</label>
              <select v-model="setting.spin_type" class="block w-full px-2 py-1 text-sm border rounded-md">
                <option value="">--Select spin type--</option>
                <option value="Light Spin">Light Spin</option>
                <option value="Heavy Spin">Heavy Spin</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end">
        <button v-if="flag !== 'requote'" class="p-1 text-white border rounded bg-custom-yellow" @click="addRow">
          <svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
            viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M5 12h14m-7 7V5" />
          </svg>
        </button>
      </div>

      <div class="py-6">
        <div v-if="flag === 'Split'" class="p-3 mb-4 rounded" :class="totalComponentVolume === (source_units.length > 1 ? totalSourceVolume : originalVolume) ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'">
          <p class="text-sm">
            <span class="font-bold">Volume Check:</span> 
            Total: {{ totalComponentVolume }} ml / Original: {{ source_units.length > 1 ? totalSourceVolume : originalVolume }} ml
            <span v-if="totalComponentVolume === (source_units.length > 1 ? totalSourceVolume : originalVolume)" class="ml-2 text-green-500">✓ Balanced</span>
            <span v-else class="ml-2 text-red-500">✗ Mismatch!</span>
          </p>
        </div>
<form @submit.prevent="handleSubmit">
          <div v-for="(row, index) in formData.rows" :key="index" class="grid grid-cols-1 gap-3 p-4 mb-4 border border-gray-200 rounded-lg md:grid-cols-8">
            <div>
              <label class="block mb-1 text-xs text-gray-700" :for="`source_unit_${index}`">Source Unit</label>
              <select v-model="row.source_unit_product_id" class="block w-full px-2 py-1 text-sm border rounded-md" @change="onSourceUnitChange(row)">
                <option value="">--Select source unit--</option>
                <option v-for="unit in source_units" :key="unit.product_id" :value="unit.product_id">
                  {{ unit.pint_serial }}
                </option>
              </select>
            </div>
            
            <div>
              <label class="block mb-1 text-xs text-gray-700" :for="`unit_product_id_${index}`">Component type <span class="text-red-500">*</span></label>
              <select v-model="row.unit_product_id" class="block w-full px-2 py-1 text-sm border rounded-md" @change="updateexpiry_date(row)">
                <option value="" disabled>--Select component type--</option>
                <option v-for="product in filteredBloodProducts" :key="product.unit_product_id" :value="product.unit_product_id">
                  {{ product.name }}({{ product.description }})
                </option>
              </select>
            </div>

            <div>
              <label class="block mb-1 text-xs text-gray-700" :for="`volume_${index}`">Volume <span class="text-red-500">*</span></label>
              <input v-model.number="row.volume" type="text" placeholder="Volume" class="block w-full px-2 py-1 text-sm border rounded-md">
            </div>

            <div>
              <label class="block mb-1 text-xs text-gray-700" :for="`pint_serial_${index}`">Serial/Bag number <span class="text-red-500">*</span></label>
              <input v-model="row.pint_serial" type="text" placeholder="Serial" class="block w-full px-2 py-1 text-sm border rounded-md" />
            </div>

            <div v-if="flag !== 'Requote'" class="col-span-2">
              <label class="block mb-1 text-xs text-gray-700" :for="`centrifuge_setting_${index}`">Centrifuge Setting</label>
              <select v-model="row.centrifuge_setting_id" class="block w-full px-2 py-1 text-sm border rounded-md">
                <option value="">--Select centrifuge--</option>
                <option v-for="(setting, idx) in centrifugeSettings" :key="idx" :value="idx">
                  {{ setting.centrifuge_batch || 'Setting ' + (idx + 1) }}
                </option>
              </select>
            </div>

            <div>
              <label class="block mb-1 text-xs text-gray-700" :for="`expiryDays_${index}`">Expiry days <span class="text-red-500">*</span></label>
              <select v-model="row.expiryDays" class="block w-full px-2 py-1 text-sm border rounded-md" @change="updateexpiry_date(row)">
                <option value="" disabled>Select</option>
                <option value="5">5 Days</option>
                <option value="6">6 Days</option>
                <option value="7">7 Days</option>
                <option value="24">24 Days</option>
                <option value="30">30 Days</option>
                <option value="35">35 Days</option>
                <option value="42">42 Days</option>
                <option value="45">45 Days</option>
                <option value="365">1 year</option>
              </select>
            </div>

            <div class="w-36">
              <label class="block mb-1 text-xs text-gray-700" :for="`expiry_date_${index}`">Expiry date <span class="text-red-500">*</span></label>
              <div class="flex items-center">
                <input v-model="row.expiry_date" type="date" class="w-full px-2 py-1 text-sm border rounded-md">
                <button type="button" class="p-1 ml-1 border rounded text-custom-yellow border-custom-yellow" @click="removeRow(index)">
                  <svg class="w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div class="flex justify-between">
        <div>
          <button class="px-4 py-2 border rounded text-custom-yellow border-custom-yellow" @click="goBack">
            Back
          </button>
        </div>
        <div>
          <button class="px-4 py-2 mr-2 border rounded text-custom-yellow border-custom-yellow" @click="goBack">
            Cancel
          </button>
          <button class="px-4 py-2 text-white rounded bg-custom-yellow disabled:opacity-50" @click="handleSubmit" :disabled="isSubmitting">
            <span v-if="isSubmitting">Submitting...</span>
            <span v-else>Submit</span>
          </button>
        </div>
      </div>
    </div>
    </div>

  </div>
</template>

<script>
import AlertLayout from '@/components/AlertLayout.vue';
import api from '@/services/api';
import SweetAlertService from '@/services/SweetAlertService';

export default {
  name: 'ComponentPreperationPage',
  components: {
    AlertLayout,
  },
  data() {
    return {
      user: '',
      facility_id: '',
      source_unit: [],
      source_units: [],
      source_product_id: '',
      flag: '',
      bloodProducts: [],
      centrifugeDevices: [],
      centrifugeSettings: [],
      lastCentrifugeSequence: 0,
      error: null,
      loading: false,
      isSubmitting: false,
      originalVolume: 0,
      totalSourceVolume: 0,
      formData: {
        rows: [
          {
            unit_product_id: '',
            volume: '',
            pint_serial: '',
            expiryDays: '',
            expiry_date: '',
            source_unit_product_id: '',
            centrifuge_setting_id: ''
          }
        ]
      },
      centrifugeForm: {
        centrifuge_device: '',
        centrifuge_code: '',
        centrifuge_batch: '',
        speed: '',
        centrifuge_time: '',
        temperature: '',
        brake: '',
        spin_type: ''
      },
    };
  },
  computed: {
    filteredBloodProducts() {
      if (this.flag === 'Requote') {
        return this.bloodProducts.filter(
          product => product.unit_product_id === this.source_unit.unit_product_id
        );
      }
      return this.bloodProducts;
    },
    totalComponentVolume() {
      return this.formData.rows.reduce((sum, row) => sum + (parseFloat(row.volume) || 0), 0);
    }
  },
  created() {
    this.source_product_id = this.$route.params.product_id;
    this.flag = this.$route.params.flag;
    this.loading = true;
  },

async mounted() {
    this.user = this.$store.getters["auth/user"];
    if (this.user && this.user.user_roles && this.user.user_roles.length > 0) {
      const facilityDept = this.user.user_roles[0].facility_department;
      if (facilityDept && facilityDept.facility) {
        this.facility_id = facilityDept.facility.facility_id;
      }
    }
    await this.fetchBloodProducts();
    await this.fetchCentrifugeDevices();
    if (this.centrifugeDevices.length > 0) {
      await this.fetchLastCentrifugeSetting(this.centrifugeDevices[0].item_id);
    } else {
      await this.fetchLastCentrifugeSetting();
    }
    await this.getBloodUnit();
  },

  methods: {
    async fetchBloodProducts() {
      try {
        const response = await api.getUnitProducts();
        this.bloodProducts = response || [];
      } catch (error) {
        this.error = 'Failed to fetch blood products';
      }
    },
    goBack() {
      this.$router.push({ name: 'ProductDetailsPage' });
    },
    async fetchCentrifugeDevices() {
      try {
        const response = await api.getInventoryRecords();
        if (response && response.data) {
          this.centrifugeDevices = response.data;
        } else if (Array.isArray(response)) {
          this.centrifugeDevices = response;
        } else {
          this.centrifugeDevices = [];
        }
      } catch (error) {
        console.error("Error fetching centrifuge devices:", error);
        this.centrifugeDevices = [];
      }
    },
    async fetchLastCentrifugeSetting(deviceId = null) {
      try {
        const params = new URLSearchParams();
        if (this.facility_id) params.append('facility_id', this.facility_id);
        if (deviceId) params.append('centrifuge_code', deviceId);
        
        const queryString = params.toString() ? `?${params.toString()}` : '';
        const response = await api.getLastCentrifugeSetting(queryString);
        
        if (response && response.centrifuge_batch) {
          this.lastCentrifugeSequence = this.extractSequenceNumber(response.centrifuge_batch);
        } else {
          this.lastCentrifugeSequence = 0;
        }
      } catch (error) {
        this.lastCentrifugeSequence = 0;
      }
    },
    extractSequenceNumber(batch) {
      if (!batch) return 0;
      const match = batch.match(/(?:A|a)(\d{4})$/);
      return match ? parseInt(match[1], 10) : 0;
    },
    handleCentrifugeDeviceChange(setting, index) {
      if (setting.centrifuge_device) {
        this.generateCentrifugeCode(setting, index);
      }
    },
    generateCentrifugeCode(setting, index) {
      const selectedDevice = this.centrifugeDevices.find(item => item.id === setting.centrifuge_device);
      if (!selectedDevice) return;
      
      const itemId = selectedDevice.item_id || 'ERS000000';
      setting.centrifuge_code = itemId;
      
      const today = new Date();
      const day = String(today.getDate()).padStart(2, '0');
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const year = String(today.getFullYear()).slice(2);

      const dateStr = `${day}${month}${year}`;
      // Increment sequence based on index in array
      const sequence = String(this.lastCentrifugeSequence + index + 1).padStart(4, '0');
      const round = `A${sequence}`;
      setting.centrifuge_batch = `${itemId}-${dateStr}-${round}`;
    },
    addCentrifugeSetting() {
      this.centrifugeSettings.push({
        centrifuge_device: '',
        centrifuge_code: '',
        centrifuge_batch: '',
        speed: '',
        centrifuge_time: '',
        temperature: '',
        brake: '',
        spin_type: ''
      });
    },
    onSourceUnitChange(row) {
      if (row.source_unit_product_id) {
        const selectedUnit = this.source_units.find(u => u.product_id === row.source_unit_product_id);
        if (selectedUnit) {
          if (selectedUnit.expiry_date) {
            row.expiry_date = new Date(selectedUnit.expiry_date).toISOString().split('T')[0];
          }
          row.pint_serial = selectedUnit.pint_serial;
        }
      }
    },
    async getBloodUnit() {
      this.loading = true;
      try {
        const sourceUnitIds = this.$route.query.source_unit_ids;
        
        if (sourceUnitIds) {
          const ids = sourceUnitIds.split(',').filter(id => id);
          this.source_units = [];
          
          for (const id of ids) {
            const response = await api.fetchSingleProduct(id);
            this.source_units.push(response.data || response);
          }
          
          this.source_unit = this.source_units[0];
          this.source_product_id = this.source_unit.product_id;
          this.totalSourceVolume = this.source_units.reduce((sum, u) => sum + (u.volume || 0), 0);
        }
        
        this.originalVolume = this.source_unit.volume;
        this.initializeRows();
      } catch (error) {
        this.error = error.message || 'Failed to fetch source unit';
      } finally {
        this.loading = false;
      }
    },
    initializeRows() {
      if (this.flag !== 'Split') {
        this.formData.rows = [{
          source_unit_product_id: this.source_unit.product_id,
          unit_product_id: this.source_unit.unit_product_id || '',
          volume: this.source_unit.volume || '',
          pint_serial: this.source_unit.pint_serial || '',
          expiryDays: '',
          expiry_date: this.source_unit.expiry_date ? new Date(this.source_unit.expiry_date).toISOString().split('T')[0] : '',
          centrifuge_setting_id: ''
        }];
        return;
      }

      const prc = this.bloodProducts.find(p => p.name && p.name.toUpperCase().includes('PRC'));
      const plts = this.bloodProducts.find(p => p.name && (p.name.toUpperCase().includes('PLT') || p.name.toUpperCase().includes('PLTS')));
      const pl = this.bloodProducts.find(p => p.name && p.name.toUpperCase() === 'PL');

      const defaultRows = [];

      if (prc) {
        defaultRows.push({
          source_unit_product_id: this.source_unit.product_id,
          unit_product_id: prc.unit_product_id,
          volume: '',
          pint_serial: this.source_unit.pint_serial || '',
          expiryDays: '',
          expiry_date: '',
          centrifuge_setting_id: ''
        });
      }

      if (plts) {
        defaultRows.push({
          source_unit_product_id: this.source_unit.product_id,
          unit_product_id: plts.unit_product_id,
          volume: '',
          pint_serial: this.source_unit.pint_serial || '',
          expiryDays: '',
          expiry_date: '',
          centrifuge_setting_id: ''
        });
      }

      if (pl) {
        defaultRows.push({
          source_unit_product_id: this.source_unit.product_id,
          unit_product_id: pl.unit_product_id,
          volume: '',
          pint_serial: this.source_unit.pint_serial || '',
          expiryDays: '',
          expiry_date: '',
          centrifuge_setting_id: ''
        });
      } else if (plts && defaultRows.length === 2) {
        defaultRows.push({
          source_unit_product_id: this.source_unit.product_id,
          unit_product_id: plts.unit_product_id,
          volume: '',
          pint_serial: this.source_unit.pint_serial || '',
          expiryDays: '',
          expiry_date: '',
          centrifuge_setting_id: ''
        });
      }

      if (defaultRows.length === 0) {
        defaultRows.push({
          source_unit_product_id: this.source_unit.product_id,
          unit_product_id: '',
          volume: this.originalVolume,
          pint_serial: this.source_unit.pint_serial || '',
          expiryDays: '',
          expiry_date: '',
          centrifuge_setting_id: ''
        });
      }

      this.formData.rows = defaultRows;

      if (this.flag === 'Split') {
        this.formData.rows.forEach(row => {
          this.updateexpiry_date(row);
        });
      }
    },
    async handleSubmit() {
      this.isSubmitting = true;
      
      const volumeToCheck = this.source_units.length > 1 ? this.totalSourceVolume : this.originalVolume;
      
      if (this.flag === 'Split') {
        const totalComponentVolume = this.formData.rows.reduce((sum, row) => sum + (parseFloat(row.volume) || 0), 0);
        if (totalComponentVolume !== volumeToCheck) {
          SweetAlertService.showError('Volume Mismatch', `The sum of component volumes (${totalComponentVolume} ml) must equal the original volume (${volumeToCheck} ml).`);
          return;
        }
      }

      // First, create any centrifuge settings
      let centrifugeSettingIds = [];
      for (const setting of this.centrifugeSettings) {
        if (setting.centrifuge_code || setting.speed || setting.centrifuge_time) {
          try {
const centrifugePayload = {
               centrifuge_setting: {
                 facility_id: this.facility_id || this.user.facility_id,
                 centrifuge_code: setting.centrifuge_code,
                 centrifuge_batch: setting.centrifuge_batch,
                 speed: setting.speed,
                 centrifuge_time: setting.centrifuge_time,
                 temperature: setting.temperature,
                 brake: setting.brake,
                 spin_type: setting.spin_type
               }
             };
            const centrifugeResponse = await api.createCentrifugeSetting(centrifugePayload);
            centrifugeSettingIds.push(centrifugeResponse.centrifuge_setting_id || centrifugeResponse.data?.centrifuge_setting_id);
          } catch (error) {
            console.error('Error creating centrifuge setting:', error);
            SweetAlertService.showError('Error', 'Error saving centrifuge settings.');
            return;
          }
        }
      }

      // Prepare rows with centrifuge_setting_id
      const rowsWithCentrifuge = this.formData.rows.map(row => ({
        ...row,
        centrifuge_setting_id: row.centrifuge_setting_id !== '' && centrifugeSettingIds[row.centrifuge_setting_id] 
          ? centrifugeSettingIds[row.centrifuge_setting_id] 
          : null
      }));

      // For multiple source units, we need to handle differently
      const sourceId = this.source_units.length > 1 
        ? this.source_units.map(u => u.product_id).join(',') 
        : this.source_unit.product_id;

      const payload = {
        source_id: sourceId,
        process_type: this.flag,
        rows: rowsWithCentrifuge.map(row => ({
          ...row
        }))
      };

      try {
        await api.componentPreparation(payload);
        await SweetAlertService.showSuccess('Success!', 'Component(s) prepared successfully!.');
        this.$router.push({ name: 'InventoryManagementPage' });
      } catch (error) {
        console.error('Error during component preparation:', error);
        SweetAlertService.showError('Error', 'Error preparing component(s).');
      } finally {
        this.isSubmitting = false;
      }
    },
    handleCancel() {
      this.resetForm();
    },
    resetForm() {
      this.formData.rows = [
        {
          unit_product_id: '',
          volume: this.originalVolume,
          pint_serial: this.source_unit.pint_serial || '',
          expiryDays: '',
          expiry_date: '',
          source_unit_product_id: '',
          centrifuge_setting_id: ''
        }
      ];
    },
    addRow() {
      const remainingVolume = this.calculateRemainingVolume();
      if (remainingVolume <= 0) {
        SweetAlertService.showWarning('Volume Limit', 'No more volume available to add.');
        return;
      }

      this.formData.rows.push({
        unit_product_id: '',
        volume: remainingVolume,
        pint_serial: '',
        expiryDays: '',
        expiry_date: '',
        source_unit_product_id: '',
        centrifuge_setting_id: ''
      });
    },
    removeRow(index) {
      this.formData.rows.splice(index, 1);
    },
    removeSourceUnit(index) {
      if (this.source_units.length <= 1) {
        SweetAlertService.showWarning('Cannot Remove', 'At least one source unit is required.');
        return;
      }
      
      this.source_units.splice(index, 1);
      
      // Update totals
      this.totalSourceVolume = this.source_units.reduce((sum, u) => sum + (u.volume || 0), 0);
      
      // If the removed unit was the primary source, update to first remaining
      if (this.source_unit.product_id === this.source_units[index]?.product_id || 
          !this.source_units.some(u => u.product_id === this.source_unit.product_id)) {
        this.source_unit = this.source_units[0];
        this.source_product_id = this.source_unit.product_id;
      }
    },
calculateRemainingVolume() {
       const totalVolumeUsed = this.formData.rows.reduce((total, row) => total + (row.volume || 0), 0);
       const sourceVolume = this.source_units.length > 1 ? this.totalSourceVolume : this.originalVolume;
       return sourceVolume - totalVolumeUsed;
     },
    calculateexpiry_date(days) {
      const today = new Date();
      today.setDate(today.getDate() + days);
      return today.toISOString().split('T')[0];
    },
    updateexpiry_date(row) {
      // Get source expiry date from selected source unit or default to primary source unit
      let sourceExpiryDate;
      if (row.source_unit_product_id) {
        const selectedUnit = this.source_units.find(u => u.product_id === row.source_unit_product_id);
        sourceExpiryDate = selectedUnit ? new Date(selectedUnit.expiry_date) : new Date(this.source_unit.expiry_date);
      } else {
        sourceExpiryDate = new Date(this.source_unit.expiry_date);
      }
      
      if (this.flag === 'Requote') {
        return;
      } else if (row.expiryDays) {
        row.expiry_date = this.calculateexpiry_date(parseInt(row.expiryDays, 10));
      } else if (row.unit_product_id) {
        const selectedProduct = this.bloodProducts.find(
          product => product.unit_product_id === row.unit_product_id
        );
        let newExpiryDate;

        if (selectedProduct) {
          switch (selectedProduct.name) {
            case 'PRC':
              newExpiryDate = sourceExpiryDate;
              break;
            case 'FFP':
              newExpiryDate = new Date(sourceExpiryDate);
              newExpiryDate.setFullYear(sourceExpiryDate.getFullYear() + 1);
              break;
            case 'PLTS':
              newExpiryDate = new Date(sourceExpiryDate);
              newExpiryDate.setDate(sourceExpiryDate.getDate() + 5);
              break;
            case 'PL':
              newExpiryDate = new Date(sourceExpiryDate);
              newExpiryDate.setFullYear(sourceExpiryDate.getFullYear() + 1);
              break;
            default:
              return;
          }
          row.expiry_date = newExpiryDate.toISOString().split('T')[0];
        }
      } else if (row.source_unit_product_id && !row.expiryDays) {
        // If source unit is selected but no expiryDays/component type, just use source expiry date
        row.expiry_date = sourceExpiryDate.toISOString().split('T')[0];
      }
    },
  },
};
</script>